/**
 * Browser-side `check_auto_segmentation_jobs`: the Contour+ jobs this
 * session started, read from the viewer's own job queue (kept current by
 * its existing status polling). For one job in connector mode it also
 * refreshes the status from the connector and reads the failure details the
 * connector stored — the same calls the viewer's job handling uses. Ports
 * are injected, so this is React-free and unit-testable.
 */
import type { CheckAutoSegmentationJobsOutput } from '../../contract';
import { asRecord } from '../core';

/** Same cap as the server contract (MAX_JOB_ERROR_CHARS). */
export const MAX_JOB_ERROR_CHARS = 600;
/** At most this many error files are read for one job. */
const MAX_ERROR_FILES = 3;

/** One Contour+ job as the viewer's job queue holds it. */
export interface ContourJobFacts {
    readonly jobId: string;
    readonly model: string;
    readonly status: string;
    readonly error?: string;
    /** Epoch ms. */
    readonly createdAt: number;
    readonly finishedAt?: number;
    readonly isImported: boolean;
}

export interface ContourJobReadDeps {
    /** False outside connector mode: no connector to refresh from. */
    readonly connectorMode: boolean;
    /** Contour+ jobs in the viewer's job queue, newest first. */
    contourJobs(): ReadonlyArray<ContourJobFacts>;
    /** `GET /api/tasks/{taskID}`: current status and stored error files. */
    taskStatus(
        jobId: string
    ): Promise<{ status?: string; errorFiles: string[] }>;
    /** `GET /api/tasks/{taskID}/errors/{errorPath}`: the files' text. */
    readErrors(jobId: string, files: string[]): Promise<string[]>;
}

/** ISO time, or empty for a missing or invalid timestamp (never throws). */
function isoTime(ms: number | undefined): string {
    const date = new Date(ms ?? Number.NaN);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

function toJob(j: ContourJobFacts) {
    return {
        jobId: j.jobId,
        model: j.model,
        status: j.status,
        ...(j.error ? { error: j.error } : {}),
        startedAt: isoTime(j.createdAt),
        ...(isoTime(j.finishedAt) ? { finishedAt: isoTime(j.finishedAt) } : {}),
        resultsLoaded: j.isImported,
    };
}

function shorten(text: string): string {
    const t = text.trim();
    return t.length > MAX_JOB_ERROR_CHARS
        ? `${t.slice(0, MAX_JOB_ERROR_CHARS)}…`
        : t;
}

export async function checkAutoSegmentationJobs(
    input: unknown,
    deps: ContourJobReadDeps
): Promise<CheckAutoSegmentationJobsOutput> {
    const raw = asRecord(input)?.jobId;
    const jobId = typeof raw === 'string' && raw ? raw : undefined;
    const jobs = deps.contourJobs();

    if (!jobId) {
        return { status: 'list', jobs: jobs.map(toJob) };
    }
    const job = jobs.find((j) => j.jobId === jobId);
    if (!job) {
        return {
            status: 'unavailable',
            reason:
                `job "${jobId}" was not started in this viewer session; ` +
                'call check_auto_segmentation_jobs without a jobId to list ' +
                'the known jobs',
        };
    }
    if (!deps.connectorMode) {
        // Backend mode: the queue's own status and error are all there is.
        return { status: 'detail', job: toJob(job), errorDetails: [] };
    }

    let connectorStatus: string | undefined;
    let errorFiles: string[] = [];
    try {
        const task = await deps.taskStatus(jobId);
        connectorStatus = task.status;
        errorFiles = task.errorFiles;
    } catch {
        return {
            status: 'detail',
            job: toJob(job),
            errorDetails: [
                'the connector could not be reached to refresh this job',
            ],
        };
    }
    let errorDetails: string[] = [];
    if (errorFiles.length > 0) {
        try {
            errorDetails = (
                await deps.readErrors(
                    jobId,
                    errorFiles.slice(0, MAX_ERROR_FILES)
                )
            )
                .map(shorten)
                .filter(Boolean);
        } catch {
            errorDetails = ['the error details could not be read'];
        }
    }
    return {
        status: 'detail',
        job: toJob(job),
        ...(connectorStatus ? { connectorStatus } : {}),
        errorDetails,
    };
}
